<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useLogger } from 'vue-logger-plugin'
import { onMounted, ref, computed } from 'vue'
import { setStoreLogger } from '../../logger.ts'
import { ProgressBar } from '@progress/kendo-vue-progressbars'
import type { IIncentiveLevel, IIncentiveLevelsResponse } from '../../types/CommonTypes'
import { DynamicReportExecute } from '../../services/CommonService'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faSuitcase, faHourglass, faUserTie } from '@fortawesome/free-solid-svg-icons'
import { Popup } from '@progress/kendo-vue-popup'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { GetImageRootUrl } from '../../stores/SettingsStore.ts'
import { UrlHelpers } from '../../utils/urlHelpers.ts'

const { t, te } = useI18n()
const logger = useLogger()
setStoreLogger(logger)
logger.debug('AOEDashboardIncentivesWithLevels.vue')

const props = defineProps<{
  reportid: string
  type: string
}>()

const imageRootUrl = ref('')
const incentiveLevels = ref<IIncentiveLevel[]>([])
const activeSlide = ref(0)
const autoSlideInterval = ref<number | null>(null)
const showProgramDetailsPopup = ref(false)

onMounted(async () => {
  logger.debug('onMounted')

  imageRootUrl.value = await GetImageRootUrl()

  DynamicReportExecute({ reportId: props.reportid }).then((response: IIncentiveLevelsResponse) => {
    if (response) {
      incentiveLevels.value = response.Items
      startAutoSlide()
    }
  })
})

const currentIncentive = computed(() => {
  if (incentiveLevels.value.length === 0) {
    return {}
  }

  const level = incentiveLevels.value[activeSlide.value]
  return {
    title: level.title,
    levelName: level.levelName,
    description: t('dashboard.incentives.' + props.reportid + '.' + level.levelNumber),
    completeMsg: t('dashboard.incentives.' + props.reportid + '.completeMsg.' + level.levelNumber),
    status: level.pointsEarned < level.goal ? t('dashboard.incentives.status.inProgress') : t('dashboard.incentives.status.completed'),
    currentPoints: level.pointsEarned,
    targetPoints: level.goal,
    levelNumber: level.levelNumber,
    criteria: level.criteria,
    pdfTitle: level.pdfTitle
  }
})

const nextSlide = () => {
  if (incentiveLevels.value.length > 0) {
    activeSlide.value = (activeSlide.value + 1) % incentiveLevels.value.length
  }
}

const prevSlide = () => {
  if (incentiveLevels.value.length > 0) {
    activeSlide.value = (activeSlide.value - 1 + incentiveLevels.value.length) % incentiveLevels.value.length
  }
}

const startAutoSlide = () => {
  // Clear any existing interval
  stopAutoSlide()

  // Only start if we have more than one slide
  if (incentiveLevels.value.length > 1) {
    autoSlideInterval.value = window.setInterval(() => {
      nextSlide()
    }, 5000) // Change slide every 5 seconds
  }
}

const stopAutoSlide = () => {
  if (autoSlideInterval.value !== null) {
    window.clearInterval(autoSlideInterval.value)
    autoSlideInterval.value = null
  }
}

const openProgramDetails = () => {
  // A site can override the link through its message file (e.g. aoe.de-at.json);
  // te() only checks the active locale, so sites without the key keep the pdfTitle behavior
  const programDetailsUrlKey = 'dashboard.incentives.' + props.reportid + '.programDetailsUrl'
  if (te(programDetailsUrlKey)) {
    const programDetailsUrl = UrlHelpers.getSafeLinkUrl(t(programDetailsUrlKey))
    if (programDetailsUrl) {
      window.open(programDetailsUrl, '_blank', 'noopener,noreferrer')
      return
    }
  }

  // In Vue 3 Composition API, computed properties need to be accessed with .value in script
  if (currentIncentive.value && currentIncentive.value.pdfTitle) {
    window.open(imageRootUrl.value + '/incentivedocs/' + currentIncentive.value.pdfTitle, '_blank')
  }
}
</script>

<template>
  <div>
  <div v-if="incentiveLevels.length > 0" class="dashboard-incentives-container">
    <div v-if="incentiveLevels.length > 0" :class="'incentive-card incentive-card-' + props.type">
      <div class="incentive-title">{{ currentIncentive.title }}</div>

      <div class="incentive-content">
        <div class="incentive-main-content">
          <div class="incentive-icon-container">
            <span class="incentive-icon">
              <font-awesome-icon v-if="props.type === 'trip'" :icon="faSuitcase" />
              <font-awesome-icon v-if="props.type === 'mso'" :icon="faUserTie" />
              <font-awesome-icon v-if="props.type === 'starter'" :icon="faHourglass" />
            </span>
          </div>
          <div class="incentive-details">
            <h3 class="incentive-subtitle">{{ currentIncentive.levelName }}</h3>
            <div :class="currentIncentive.currentPoints && currentIncentive.targetPoints && currentIncentive.currentPoints < currentIncentive.targetPoints ? 'incentive-status-container incentive-status-container-in-progress' : 'incentive-status-container incentive-status-container-completed'">
              <span :class="currentIncentive.currentPoints && currentIncentive.targetPoints && currentIncentive.currentPoints < currentIncentive.targetPoints ? 'incentive-status-dot incentive-status-dot-in-progress' : 'incentive-status-dot incentive-status-dot-completed'">●</span>
              <span :class="currentIncentive.currentPoints && currentIncentive.targetPoints && currentIncentive.currentPoints < currentIncentive.targetPoints ? 'incentive-status incentive-status-in-progress' : 'incentive-status incentive-status-completed'">{{ currentIncentive.status }}</span>
            </div>
          </div>
        </div>
        <div class="incentive-description">
          {{ currentIncentive.description }}
        </div>
        <div class="incentive-completed" v-if="currentIncentive.currentPoints && currentIncentive.targetPoints && currentIncentive.currentPoints >= currentIncentive.targetPoints">
          <div
            v-html="currentIncentive.completeMsg">
          </div>
        </div>
        <div class="progress-section">
          <div class="progress-container travel-progress">
            <ProgressBar
              v-if="currentIncentive.currentPoints !== undefined && currentIncentive.targetPoints !== undefined"
              :value="currentIncentive.currentPoints / currentIncentive.targetPoints * 100"
              :labelVisible="false"
            />
          </div>
          <p class="incentive-points"><b>{{ currentIncentive.currentPoints }}</b> / {{ currentIncentive.targetPoints }} {{ currentIncentive.criteria }}</p>
        </div>

        <div class="carousel-controls">
          <div class="carousel-indicators" v-if="incentiveLevels.length > 0">
            <span
              v-for="(_, index) in incentiveLevels.length"
              :key="index"
              :class="['indicator', { active: activeSlide === index }]"
              @click="activeSlide = index; stopAutoSlide()"
            ></span>
          </div>

          <div class="carousel-navigation">
            <button class="carousel-nav-btn prev" @click="prevSlide; stopAutoSlide()" :disabled="incentiveLevels.length <= 1">
              &lt;
            </button>
            <button class="carousel-nav-btn next" @click="nextSlide; stopAutoSlide()" :disabled="incentiveLevels.length <= 1">
              &gt;
            </button>
          </div>
        </div>
      </div>

      <div class="incentive-footer">
        <button @click="openProgramDetails()" class="btn-program-details">{{ t('dashboard.incentives.travel.programDetails') }}</button>
      </div>
    </div>

    <Popup
      :anchor="'btnProgramDetails'"
      :show="showProgramDetailsPopup"
      :popup-class="'aoe-incentive-popup-content'"
    >
    <div class="row mt-3">
        <div
          class="col-12 popover-incentive-information"
          v-html="t('dashboard.incentives.' + props.reportid + '.programDetails')">
        </div>
        <div class="close-popup">
          <KButton
            :fill-mode="'clear'"
            @click="showProgramDetailsPopup = false"
            class="popupCloseButton"
            >&#x2715;</KButton
          >
        </div>
      </div>
    </Popup>
  </div>
  </div>
</template>

<style lang="scss">

.aoe-incentive-popup-content
  {
    position: fixed;
    top: 50%;
    left: 50%;
    padding: $card-padding;
    transform: translate(-50%, -50%);
    height: 400px;
    width: 400px;
  }

  .popupCloseButton
  {
    color: black;
    transform: scale(1.5);
  }

  .close-popup {
    text-align: right;
    position: absolute;
    top: 12px;
}

.dashboard-incentives-container {
  background-color: var(--vt-c-white);
  border-radius: $radius-md;
  box-shadow: 0 10px 15px rgba(black, 0.1);
  height: 100%;
}

.incentive-card {
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: white;
}

.incentive-title {
  @include typography-h2;
  @include font-weight-semibold;
  padding: $spacing-4;
  margin: 0;
  background-color: #f5faf7;
  text-align: left;
  border-bottom: 1px solid #e9ecef;
}

.incentive-content {
  padding: $spacing-4;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: $spacing-5;
}

.incentive-main-content {
  display: flex;
  align-items: flex-start;
  gap: $spacing-4;
}

.incentive-icon-container {
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--buttonPrimaryColor);
}

.incentive-icon {
  @include typography-display;
}

.incentive-details {
  flex: 1;
}

.incentive-subtitle {
  @include typography-h4;
  @include font-weight-semibold;
  margin: 0 0 5px 0;
}

.incentive-status-container {
  display: flex;
  align-items: center;
  gap: $spacing-1;
  margin-bottom: 10px;
  width: fit-content;
  padding-left: 10px;
  border-radius: 20%;
  padding-right: 10px;
}

.incentive-status-container-in-progress {
  background: var(--lightStandardColor);
}

.incentive-status-container-completed {
  background: var(--lightGreenColor);
}

.incentive-status-dot-in-progress {
  @include typography-caption;
  color: var(--standardColor);
}

.incentive-status-dot-completed {
  @include typography-caption;
  color: var(--greenColor);
}

.incentive-status-in-progress {
  @include typography-small;
  color: var(--standardColor);
}

.incentive-status-completed {
  @include typography-small;
  color: var(--greenColor);
}

.incentive-description {
  @include typography-body;
  line-height: 1.4;
}

.progress-container {
  margin-bottom: 5px;
}

.incentive-points {
  @include typography-small;
  text-align: right;
  margin: $spacing-1 0 0;
}

.carousel-controls {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 10px;
}

.carousel-indicators {
  display: flex;
  justify-content: center;
  gap: $gap-sm;
  margin-bottom: 10px;
  position: relative;
}

.indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #e0e0e0;
  cursor: pointer;
}

.indicator.active {
  background-color: var(--buttonPrimaryColor);
}

.carousel-navigation {
  display: none; /* Hide navigation buttons as they're not in the image */
}

.incentive-footer {
  padding: $spacing-4;
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #e9ecef;
}

.btn-program-details {
  padding: $spacing-2 $spacing-4;
  border: 1px solid #e0e0e0;
  border-radius: $radius-sm;
  @include typography-small;
  background-color: white;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-program-details:hover {
  background-color: var(--vt-c-white-soft);
  border-color: #cccccc;
}

.travel-progress :deep(.k-progressbar) {
  height: 6px;
  background-color: #e0e0e0;
  border-radius: $radius-sm;
}

.travel-progress :deep(.k-progressbar-fill) {
  background-color: var(--buttonPrimaryColor);
  border-radius: $radius-sm;
}
</style>

